import { useQuery } from "@tanstack/react-query";
import { httpRequest } from "@/lib";
import type { AiReadinessFacts } from "@/lib/ai-readiness";

export type TenantAiSetting = {
    llm_provider: string;
    llm_model: string | null;
    is_active: boolean;
    /**
     * Whether a model key for the clinic is stored (CliniSharp issues it,
     * ADR-0028). The key itself never reaches the browser (SEC-AIKEY-01): the
     * API hides it and answers this flag.
     */
    has_api_key: boolean;
    /**
     * CRED-COST-01 (ADR-0028 decision 10): the facts "IA pronta" needs. Absent
     * on an api older than CRED-COST-01; the screen then confirms nothing.
     */
    ai_readiness?: AiReadinessFacts | null;
    /**
     * `false` when CliniSharp switched the clinic's AI off. The clinic cannot
     * switch it back on; the API refuses `is_active = true` while this is false.
     */
    platform_allowed: boolean;
    /** null = follow the platform default; 0 = unlimited (slice 3 §4.4). */
    command_center_agent_replies_per_hour?: number | null;
    /** Read-only echo of `config('command_center.agent_replies_per_hour')`. */
    command_center_agent_replies_per_hour_default?: number;
};

/**
 * `tenant_ai_wallets.credit_balance` is a `decimal(10,4)` with no Eloquent
 * cast (the ledger asserts six decimals and a cast would round every read), so
 * the API answers with the string PDO produced — "25.0000" — not a number.
 * The field was called `balance` until 2026-02-21; the web kept reading the old
 * name until SCRUM-215, which is why the Copilot page always showed zero.
 */
export type TenantAiWallet = {
    credit_balance: number | string | null;
    is_active?: boolean;
};

export type AiSettingsResponse = {
    setting: TenantAiSetting;
    wallet: TenantAiWallet;
};

/**
 * The credit balance in USD, or `null` when the payload does not carry a
 * number. Never 0 by default: a zero balance stops the Copilot answering, so
 * an unreadable balance must not be printed as one.
 */
export function parseCreditBalance(
    wallet: TenantAiWallet | undefined | null
): number | null {
    if (!wallet || wallet.credit_balance === null || wallet.credit_balance === undefined) {
        return null;
    }
    const value = Number(wallet.credit_balance);
    return Number.isFinite(value) ? value : null;
}

async function fetchAiSettings(): Promise<AiSettingsResponse> {
    const response = await httpRequest.get<AiSettingsResponse>("/tenant-ai-settings");
    return response.data;
}

export function useFetchAiSettingsQuery() {
    const query = useQuery({
        queryKey: ["tenant-ai-settings"],
        queryFn: fetchAiSettings,
    });
    return {
        ...query,
        data: query.data,
    };
}
